import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import { AdminAPI, getFragment, SharedAPI } from '@guestgoodie/graphql'

import { ExistingFile, GoodieFile, isNewFile } from '../types'
import { uploadToS3 } from '../utils/uploadHelpers'
import { cleanCaptions } from '../utils/captions'
import { getImageDimensions } from '../utils/size'

export default function useUploadFile(): [
  (
    file: GoodieFile,
    onProgress?: (progress: number) => void
  ) => Promise<ExistingFile>,
  loading: boolean
] {
  const [create] = useMutation(AdminAPI.file.CREATE)
  const [finalizeUpload] = useMutation(AdminAPI.file.FINALIZE_UPLOAD)
  const [loading, setLoading] = useState(false)

  const upload = useCallback(
    (newFile: GoodieFile, onProgress?: (progress: number) => void) => {
      if (!isNewFile(newFile)) return Promise.resolve(newFile)

      setLoading(true)

      return getImageDimensions(newFile.file)
        .then(({ width, height }) =>
          create({
            variables: {
              input: {
                name: newFile.file.name,
                contentType: newFile.file.type,
                alwaysMute: newFile.alwaysMute,
                altText: newFile.altText,
                translations: newFile.translations,
                width,
                height,
                captions: newFile.captions
                  ? cleanCaptions(newFile.captions)
                  : undefined
              }
            }
          })
        )
        .then(({ data, errors }) => {
          if (!data) return Promise.reject(errors)

          const { id, url } = data.createFile

          const apiUploadPromise = uploadToS3(newFile.file, url, onProgress)

          return apiUploadPromise.then(() => Promise.resolve(id))
        })
        .then(id => {
          return finalizeUpload({
            variables: {
              input: { id, processingMetadata: newFile.processingMetadata }
            }
          }).then(({ data, errors }) => {
            if (!data) return Promise.reject(errors)

            const file = getFragment(
              SharedAPI.file.FILE_FRAGMENT,
              data.finalizeUpload
            )

            return Promise.resolve(file)
          })
        })
        .finally(() => setLoading(false))
    },
    [create, finalizeUpload, setLoading]
  )

  return [upload, loading]
}
